import { useMemo, useState, useEffect } from "react"
import { Modal, Text } from "@mantine/core"
import { useModals } from "@mantine/modals"
import { useNotifications } from "@mantine/notifications"
import { SelectColumnFilter } from "components/ui/table/TableHeader"
import { EditActions } from "components/ui/table/TableUtils"
import { Icon } from "@material-tailwind/react"
import { getAll, deleteByID } from "services/api.service"
import useFetchAndLoad from "hooks/useFetchAndLoad.ts"
import Table from "components/ui/table/Table"
import UserForm from "components/ui/forms/UserForm"

const Users = () => {
  const emptyUser = {
    first_name: "",
    last_name: "",
    email: "",
    access_level: [],
    worksite: [],
    password: "",
  }

  const API_ENDPOINT = "/users"

  const [users, setUsers] = useState([])
  const [isCreation, setIsCreation] = useState(false)
  const [refresh, setRefresh] = useState(false)
  const [editUserData, setEditUserData] = useState(emptyUser)
  const [opened, setOpened] = useState(false)

  const modals = useModals()
  const notifications = useNotifications()

  const { loading, callEndpoint } = useFetchAndLoad()

  const setData = async () => {
    await callEndpoint(getAll(API_ENDPOINT))
      .then((response) => {
        if (response.status === 200 || response.status === 201) {
          setUsers(response.data.data.map((user) => ({ ...user, access_level: "Guardia" })))
        }
      })
      .catch((error) => {
        notifications.showNotification({
          color: "red",
          title: "Error",
          message: "Error al obtener a los empleados",
        })
      })
  }

  useEffect(() => {
    setData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refresh])

  const onEdit = (user) => {
    setEditUserData(user.original)
    setIsCreation(false)
    setOpened(true)
  }

  const onDelete = (user) => {
    openConfirmDeleteModal(user)
  }

  const openConfirmDeleteModal = (user) => {
    modals.openConfirmModal({
      title: "Por favor confirma la eliminación",
      children: (
        <Text size='sm'>
          ¿Seguro de que deseas eliminar al usuario
          <span className='font-bold'>
            {" " + user.original.first_name} {user.original.last_name_1} {user.original.last_name_2}
          </span>
          ? <br />
          Esta acción no se puede deshacer.
        </Text>
      ),
      labels: { confirm: "Eliminar", cancel: "Cancelar" },
      confirmProps: {
        className: "bg-red-600 text-white hover:bg-red-500",
      },
      onCancel: () => {},
      onConfirm: async () => {
        notifications.showNotification({
          color: "blue",
          title: "Eliminando",
          loading: true,
          message: "Eliminando usuario.",
        })
        await callEndpoint(deleteByID(API_ENDPOINT, user.original._id))
          .then((response) => {
            if (response.status === 200 || response.status === 201) {
              setData()
              notifications.clean()
              notifications.showNotification({
                color: "green",
                title: "Usuario eliminado",
                message: "El usuario ha sido eliminado con éxito.",
                icon: <Icon name={"check"} />,
              })
            }
          })
          .catch((error) => {
            notifications.clean()
            notifications.showNotification({
              color: "red",
              title: "Error",
              message: "Error al eliminar al usuario.",
              icon: <Icon name={"close"} />,
            })
          })
      },
    })
  }

  const columns = useMemo(
    () => [
      {
        Header: "Nombre",
        accessor: "first_name",
      },
      {
        Header: "Apellido",
        accessor: "last_name",
        Filter: SelectColumnFilter,
        filter: "includes",
      },
      {
        Header: "Correo",
        accessor: "email",
      },
      {
        Header: "Nivel de permisos",
        accessor: "access_level",
      },

      {
        Header: "Obras con acceso",
        accessor: "worksite.name",
      },
      ,
      {
        Header: "Acciones",
        Cell: ({ row }) => <EditActions value={row} onEdit={onEdit} onDelete={onDelete} />,
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )

  return (
    <>
      <Table
        columns={columns}
        data={users}
        iconName={"person"}
        editable={true}
        setOpened={setOpened}
        setIsCreation={setIsCreation}
        loading={loading}
        header={"Usuarios"}
      />

      <Modal
        opened={opened}
        onClose={() => {
          setEditUserData(emptyUser)
          setOpened(false)
        }}
        title={isCreation ? "Crear Usuario" : "Editar Usuario"}
        size='xl'>
        <UserForm
          data={editUserData}
          refresh={() => {
            setRefresh(!refresh)
            setOpened(false)
            setEditUserData(emptyUser)
          }}
          isCreation={isCreation}
          notifications={notifications}
          endpoint={API_ENDPOINT}
        />
      </Modal>
    </>
  )
}

export default Users
